"use client";

import { Home, RefreshCw, ServerCrash } from "lucide-react";
import { Poppins } from "next/font/google";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

const poppins = Poppins({
	subsets: ["latin"],
	weight: ["400", "500", "600", "700", "800", "900"],
	variable: "--font-poppins",
	display: "swap",
});

export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	useEffect(() => {
		console.error("Global application error:", error);
	}, [error]);

	return (
		<html lang="en" className={poppins.className}>
			<head>
				<title>Critical Error | Ice Age Meals</title>
				<meta name="robots" content="noindex, nofollow" />
			</head>
			<body className="font-sans antialiased min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
				<div className="text-center max-w-lg mx-auto px-6 py-16 space-y-8">
					<div className="flex justify-center">
						<div className="w-32 h-32 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center">
							<ServerCrash className="w-16 h-16 text-red-500 dark:text-red-400" />
						</div>
					</div>

					<div className="space-y-3">
						<h1 className="text-4xl font-black tracking-tight">
							Critical Error
						</h1>
						<p className="text-zinc-500 dark:text-zinc-400 text-lg leading-relaxed">
							Something went seriously wrong. Our team has been notified and
							we&apos;re working to fix this as soon as possible.
						</p>
						{error.digest && (
							<p className="text-xs text-zinc-400 dark:text-zinc-600 font-mono">
								Error ID: {error.digest}
							</p>
						)}
					</div>

					<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
						<Button onClick={reset} size="lg" className="gap-2">
							<RefreshCw className="w-4 h-4" />
							Try Again
						</Button>
						<Button variant="outline" size="lg" asChild className="gap-2">
							<a href="/">
								<Home className="w-4 h-4" />
								Go to Homepage
							</a>
						</Button>
					</div>
				</div>
			</body>
		</html>
	);
}
